import React from 'react';

export interface SliderMark {
  value: number;
  label?: string;
}

export interface SliderProps {
  label?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  valueFormatter?: (val: number) => string;
  onChange: (val: number) => void;
  disabled?: boolean;
  marks?: SliderMark[];
  description?: string;
  className?: string;
}

export const Slider: React.FC<SliderProps> = ({
  label,
  value,
  min,
  max,
  step = 1,
  unit = '',
  valueFormatter,
  onChange,
  disabled = false,
  marks,
  description,
  className = '',
}) => {
  const percentage = Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));

  const formattedDisplayValue = valueFormatter ? valueFormatter(value) : `${value}${unit ? ` ${unit}` : ''}`;

  return (
    <div className={`w-full flex flex-col space-y-2 select-none ${className}`}>
      {(label || value !== undefined) && (
        <div className="flex items-center justify-between">
          {label && (
            <label className="text-xs sm:text-sm font-medium text-text-primary flex items-center gap-1.5">
              {label}
            </label>
          )}
          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold bg-surface-hover text-accent-emerald border border-white/10 font-mono">
            {formattedDisplayValue}
          </span>
        </div>
      )}

      {description && <p className="text-[11px] sm:text-xs text-text-secondary">{description}</p>}

      <div className="relative flex items-center py-2 min-h-[44px]">
        {/* Track Background */}
        <div className="relative w-full h-2 rounded-full bg-slate-800 border border-white/5 overflow-hidden">
          {/* Active Fill with gradient */}
          <div
            className="h-full bg-gradient-to-r from-accent-emerald to-accent-cyan rounded-full transition-all duration-75"
            style={{ width: `${percentage}%` }}
          />
        </div>

        {/* Real Native Range Input over top for 100% accessibility & touch events */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(Number(e.target.value))}
          className="
            absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed
            focus:outline-none
          "
          aria-label={label || 'Значення слайдера'}
          aria-valuenow={value}
          aria-valuemin={min}
          aria-valuemax={max}
        />

        {/* Custom Visual Thumb */}
        <div
          className="
            absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-5 h-5 rounded-full
            bg-slate-900 border-2 border-accent-emerald shadow-[0_0_12px_rgba(16,185,129,0.5)]
            pointer-events-none transition-all duration-75
          "
          style={{ left: `${percentage}%` }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-accent-neon mx-auto mt-1" />
        </div>
      </div>

      {marks && marks.length > 0 && (
        <div className="relative w-full flex justify-between px-1 text-[10px] text-text-muted">
          {marks.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => onChange(m.value)}
              className={`hover:text-accent-emerald transition-colors ${
                value === m.value ? 'text-accent-emerald font-semibold' : ''
              }`}
            >
              {m.label ?? m.value}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
